/**
 * Browser GraphQL client factory — the CLIENT-SAFE half of
 * `@/lib/graphql/graphql-clients`.
 *
 * Split out because `graphql-clients.ts` imports `@/lib/audit/attribution-context`,
 * which imports `node:async_hooks`. `src/lib/providers.tsx` (the root
 * `ApolloProvider`, a `"use client"` module mounted on every page) needs the
 * browser factory, and reaching it THROUGH `graphql-clients.ts` pulled
 * `node:async_hooks` into the browser graph. Turbopack refuses that at build
 * time ("the chunking context (unknown) does not support external modules
 * (request: node:async_hooks)") — `tsc` and jest cannot see it. Keep this file
 * free of server-only imports; `tests/graphql/browser-graphql-client-boundary.test.ts`
 * walks its import graph to hold that line.
 */

import { createBrowserGraphQLClient as sharedCreateBrowserGraphQLClient } from "@west-tennessee-consulting/supabase-graphql-kit";
import { createClient as createSupabaseClient } from "@supabase/supabase-js";
import { logger } from "@/lib/logger";

/**
 * Hub's `DeclareDefaultOptions` augmentation requires exactly this shape on
 * every `ApolloClient` in the program — see
 * `apps/hub/types/apollo-client-augmentation.d.ts`.
 */
export const hubDefaultOptions = {
  watchQuery: { errorPolicy: "all" as const },
  query: { errorPolicy: "all" as const },
};

/**
 * In-memory cache of the current Supabase access token, shared by every
 * `createClientGraphQLClient()` instance in this process.
 *
 * Awaiting `supabase.auth.getSession()` on EVERY GraphQL operation was a
 * per-request tax every CRM list query paid on the hot path. Seed the token
 * once (lazily, on the first op) and keep it current via `onAuthStateChange`
 * (fires on INITIAL_SESSION, TOKEN_REFRESHED, SIGNED_OUT); after that, reads
 * are synchronous with no session lookup.
 */
let cachedAccessToken: string | null = null;
let authListenerAttached = false;

/**
 * Narrow structural type for exactly the one method this needs — NOT
 * `ReturnType<typeof createSupabaseClient>`. Two `@supabase/supabase-js`
 * copies exist in the dependency graph and their generated `SupabaseClient`
 * generics are nominally incompatible; a narrow structural type sidesteps it.
 */
interface AuthStateSource {
  auth: {
    onAuthStateChange: (
      callback: (
        event: string,
        session: { access_token?: string | null } | null,
      ) => void,
    ) => unknown;
  };
}

function attachAuthListener(supabase: AuthStateSource): void {
  if (authListenerAttached) return;
  authListenerAttached = true;
  supabase.auth.onAuthStateChange((_event, session) => {
    cachedAccessToken = session?.access_token ?? null;
  });
}

/**
 * Create a client-side GraphQL client that uses the user's authentication token
 * This respects RLS policies and should be used for all user-facing operations
 */
export function createClientGraphQLClient() {
  return sharedCreateBrowserGraphQLClient(
    {
      url: process.env.NEXT_PUBLIC_SUPABASE_URL!,
      anonKey: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
      getAccessToken: async () => {
        // Browser-only — never attempt a session lookup during SSR.
        if (typeof window === "undefined") return null;
        try {
          const supabase = createSupabaseClient(
            process.env.NEXT_PUBLIC_SUPABASE_URL!,
            process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
          );
          attachAuthListener(supabase);

          // Fast path: the token `onAuthStateChange` kept current.
          if (cachedAccessToken) return cachedAccessToken;

          // Cold path: seed once via getSession so this request still carries
          // auth; every later op hits the fast path above.
          const { data, error } = await supabase.auth.getSession();
          if (error) logger.error("GraphQL Auth Error", error);
          cachedAccessToken = data.session?.access_token ?? null;
          return cachedAccessToken;
        } catch (authError) {
          logger.error("Failed to get auth session", authError);
          return null;
        }
      },
    },
    {
      defaultOptions: hubDefaultOptions,
    },
  );
}
